/**
 * Textos es-PE de Staff (T018/T019, implementation-notes 002 §4; mismo patrón que el Admin).
 *
 * Un JSON por feature en `src/i18n/es-PE/<feature>.json`; el nombre del archivo es el espacio de
 * nombres de la clave: `common.json` → `t('common.buttons.continue')`, `activation.json` →
 * `t('activation.…')`, `access.json` → `t('access.…')`. No edites el JSON de otra feature. Las
 * claves ausentes devuelven la propia clave para que el hueco se vea en pantalla.
 */
export const LOCALE = 'es-PE';

export type TranslationVars = Record<string, string | number>;

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

const modules = import.meta.glob<Record<string, JsonValue>>('./es-PE/*.json', {
  eager: true,
  import: 'default',
});

function flatten(value: JsonValue, prefix: string, target: Map<string, string>): void {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    target.set(prefix, String(value));
    return;
  }
  for (const [key, child] of Object.entries(value)) {
    flatten(child, prefix ? `${prefix}.${key}` : key, target);
  }
}

function buildDictionary(): Map<string, string> {
  const dictionary = new Map<string, string>();
  for (const [path, content] of Object.entries(modules)) {
    const namespace = path.replace(/^.*\//, '').replace(/\.json$/, '');
    flatten(content as JsonValue, namespace, dictionary);
  }
  return dictionary;
}

const dictionary = buildDictionary();

/** Todas las claves disponibles (útil en pruebas). */
export function translationKeys(): string[] {
  return [...dictionary.keys()].sort();
}

/** Reemplaza `{variable}` por su valor; deja el marcador intacto si no se pasó la variable. */
export function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

/** Texto para una clave `namespace.ruta.a.la.clave`. */
export function t(key: string, vars?: TranslationVars): string {
  const template = dictionary.get(key);
  if (template === undefined) {
    return key;
  }
  return interpolate(template, vars);
}

// ---------------------------------------------------------------------------
// Fechas y horas: siempre es-PE, 24 h, en la zona indicada (la de la sede del trabajador).
// ---------------------------------------------------------------------------

function formatter(options: Intl.DateTimeFormatOptions, timeZone?: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(LOCALE, {
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
    ...options,
  });
}

/** `2026-09-17T13:05:00Z` + `America/Lima` → `17/09/2026, 08:05`. */
export function formatDateTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return formatter(
    { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' },
    timeZone,
  ).format(date);
}

/** `2026-09-17T13:05:00Z` + `America/Lima` → `17/09/2026`. */
export function formatDate(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return formatter({ year: 'numeric', month: '2-digit', day: '2-digit' }, timeZone).format(date);
}

/** `2026-09-17T13:05:00Z` + `America/Lima` → `08:05`. */
export function formatTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return formatter({ hour: '2-digit', minute: '2-digit' }, timeZone).format(date);
}

/**
 * "Vence hoy a las 23:10" / "Vence mañana a las 23:10" (005 · QA-9): el código de traspaso solo
 * decía la hora ("Vence a las 23:10"), que se leyó como fecha ("23/10"). Sin `timeZone` usa la zona
 * horaria local del teléfono (el código se muestra antes de tener sesión, sin sede de la que sacar
 * un huso). `now` es inyectable para pruebas.
 */
export function formatExpiryDayTime(iso: string, timeZone?: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const dayKey = formatter({ year: 'numeric', month: '2-digit', day: '2-digit' }, timeZone);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  let day: string;
  if (dayKey.format(date) === dayKey.format(now)) {
    day = t('activation.install.expiresToday');
  } else if (dayKey.format(date) === dayKey.format(tomorrow)) {
    day = t('activation.install.expiresTomorrow');
  } else {
    day = t('activation.install.expiresOtherDay', { date: formatDate(iso, timeZone) });
  }

  return t('activation.install.codeExpires', { day, time: formatTime(iso, timeZone) });
}

// ---------------------------------------------------------------------------
// 006 · T018 (research R-6, R-8, R-26): fecha larga, "Hoy"/"Ayer", horas trabajadas y saludo.
// "Qué día es hoy" y la hora del saludo SIEMPRE vienen del servidor (`today`, `server_time`) y se
// leen en la zona de la sede principal: nunca `new Date()` del teléfono (wiring 006 §0.3).
// ---------------------------------------------------------------------------

const LOCAL_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` → mediodía UTC de ese día (a salvo de cualquier corrimiento de zona). */
function parseLocalDay(day: string): Date | null {
  const match = LOCAL_DAY_PATTERN.exec(day);
  if (!match) {
    return null;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  return Number.isNaN(date.getTime()) ? null : date;
}

function capitalize(text: string): string {
  return text.charAt(0).toLocaleUpperCase(LOCALE) + text.slice(1);
}

/**
 * Fecha larga con la primera letra en mayúscula: "Miércoles, 17 de septiembre".
 *
 * - Con un día operativo `YYYY-MM-DD` (historial) se formatea ese día de calendario tal cual: ya
 *   viene calculado por el servidor en la zona que corresponde, y `timeZone` se ignora.
 * - Con un instante ISO (`server_time` de la home) se formatea en `timeZone` (sede principal).
 */
export function formatWeekdayDate(isoOrDay: string, timeZone?: string): string {
  const day = parseLocalDay(isoOrDay);
  const date = day ?? new Date(isoOrDay);
  if (Number.isNaN(date.getTime())) {
    return isoOrDay;
  }
  const text = new Intl.DateTimeFormat(LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: day ? 'UTC' : timeZone,
  }).format(date);
  return capitalize(text);
}

/** Suma `delta` días a un `YYYY-MM-DD` (puro, sin zona horaria): `shiftDay('2026-03-01', -1)` → `'2026-02-28'`. */
export function shiftDay(day: string, delta: number): string {
  const date = parseLocalDay(day);
  if (!date) {
    return day;
  }
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

/** "Hoy" / "Ayer" / "Lunes, 15 de septiembre", con `today` = día operativo que manda el servidor. */
export function dayLabel(day: string, today: string): string {
  if (day === today) {
    return t('shell.days.today');
  }
  if (day === shiftDay(today, -1)) {
    return t('shell.days.yesterday');
  }
  return formatWeekdayDate(day);
}

/** Horas brutas del día: `45` → "45 min", `564` → "9 h 24 min", `480` → "8 h". */
export function formatWorkedMinutes(totalMinutes: number): string {
  const safe = Math.max(0, Math.floor(totalMinutes));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  if (hours === 0) {
    return t('shell.duration.minutes', { minutes });
  }
  if (minutes === 0) {
    return t('shell.duration.hours', { hours });
  }
  return t('shell.duration.hoursMinutes', { hours, minutes });
}

export type GreetingKey = 'morning' | 'afternoon' | 'evening';

/**
 * Franja del saludo con la hora del **servidor** en la zona de la sede principal (R-6): "Buenos
 * días" 00:00–11:59, "Buenas tardes" 12:00–18:59 y "Buenas noches" desde las 19:00.
 * `server_time` ya viene validado por el esquema; si aun así no se pudiera leer, se asume la
 * mañana (nunca se cae a la hora del teléfono).
 */
export function greetingKey(serverTimeIso: string, timeZone?: string): GreetingKey {
  const date = new Date(serverTimeIso);
  if (Number.isNaN(date.getTime())) {
    return 'morning';
  }
  const hourPart = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  })
    .formatToParts(date)
    .find((part) => part.type === 'hour');
  const hour = Number(hourPart?.value ?? Number.NaN) % 24;
  if (Number.isNaN(hour) || hour < 12) {
    return 'morning';
  }
  if (hour < 19) {
    return 'afternoon';
  }
  return 'evening';
}
